// src/stockreport/StockReport.jsx

import React, { useEffect, useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import { STOCK_REPORT_ENDPOINT, MATERIAL_LIST_ENDPOINT, GET_LOADING_POINTS_ENDPOINT } from '../../config/api';
import { decryptData } from '../../utils/encrypt';

function StockReport() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [materialId, setMaterialId] = useState('');
  const [packingType, setPackingType] = useState('');
  const [loadingPoint, setLoadingPoint] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);

  const [orgId, setOrgId] = useState(null);
  const [materialList, setMaterials] = useState([]);
  const [loadingPointList, setLoadingPointList] = useState([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('suyog_user');
      if (!stored) {
        setError("User not logged in.");
        return;
      }

      const user = decryptData(stored);
      const orgId = user?.selected_org?.value;

      if (!orgId) {
        setError("Missing organization ID.");
        return;
      }

      setOrgId(orgId);
      fetchStockData(orgId, { date_from: firstDay, date_to: today });
      fetchMaterials(orgId);
      fetchLoadingPoints(orgId);
    } catch (err) {
      setError("Failed to read user/org ID.");
    }
  }, []);

  const fetchStockData = async (org_id, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(STOCK_REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id, ...filters })
      });

      const result = await res.json();
      if (result.success) {
        setStocks(result.records || []);
      } else {
        setError(result.error || 'Failed to load stock report.');
      }
    } catch (err) {
      setError(err.message || 'Network error.');
    }
    setLoading(false);
  };

  const fetchMaterials = async () => {
    try {
         const res = await fetch(MATERIAL_LIST_ENDPOINT, {
           method: 'POST',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({})
         });
         const data = await res.json();
         if (Array.isArray(data)) setMaterials(data);
       } catch (err) {
         console.error('Material fetch error:', err);
       }
  };

  const fetchLoadingPoints = async (org_id) => {
    try {
      const res = await fetch(GET_LOADING_POINTS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id })
      });
      const result = await res.json();
      if (result.success) {
        setLoadingPointList(result.records || []);
      }
    } catch (err) {
      console.error('Failed to load loading points:', err);
    }
  };

  const handleSearch = () => {
    if (!orgId) {
      setError("Missing organization ID");
      return;
    }
    const filters = {
      material_id: materialId,
      packing_type: packingType,
      loading_point: loadingPoint,
      date_from: dateFrom,
      date_to: dateTo
    };
    fetchStockData(orgId, filters);
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4 className="mb-3">Stock Report</h4>

        <div className="card p-3 mb-4">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Material</label>
              <select className="form-select" value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
                <option value="">-- All Materials --</option>
                {materialList.map(mat => (
                  <option key={mat.id} value={mat.id}>{mat.material_name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Packing Type</label>
              <select className="form-select" value={packingType} onChange={(e) => setPackingType(e.target.value)}>
                <option value="">-- All Types --</option>
                <option value="25 Kgs Bags">25 Kgs Bags</option>
                <option value="50 Kgs Bag">50 Kgs Bag</option>
                <option value="Loose Bulker">Loose Bulker</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Loading Point</label>
              <select className="form-select" value={loadingPoint} onChange={(e) => setLoadingPoint(e.target.value)}>
                <option value="">-- All Points --</option>
                {loadingPointList.map(point => (
                  <option key={point.id} value={point.id}>{point.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date From</label>
              <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Date To</label>
              <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={handleSearch}>Search</button>
            </div>
          </div>
        </div>

        {loading && <div className="alert alert-info">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && stocks.length === 0 && <div>No stock records found.</div>}

        {stocks.length > 0 && (
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Material</th>
                  <th>Opening</th>
                  <th>Production</th>
                  <th>Issued</th>
                  <th>Closing</th>
                </tr>
              </thead>
              <tbody>
                {stocks.map((item, index) => (
                  <tr key={`${item.material_id}-${item.date}`}>
                    <td>{index + 1}</td>
                    <td>{item.date}</td>
                    <td>{item.material_name}</td>
                    <td>{item.opening_qty}</td>
                    <td>{item.production_qty}</td>
                    <td>{item.issued_qty}</td>
                    <td>{item.closing_qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default StockReport;